import React from "react";

import { Box, Theme, Tooltip, Typography } from "@mui/material";
import { getPillColorValue } from "utils";

export type TFunctionsLabels = {
    id: string | number;
    label: string;
    name: string | null;
    weight?: number;
};

export type THierarchy = {
    id: number;
    name: string;
    level: string;
};

export type TCompoundPillProps = {
    functions: TFunctionsLabels[] | null;
    hierarchy: THierarchy | string | null;
};

export const CompoundPill = ({ functions, hierarchy }: TCompoundPillProps) => {
    const functionsLabels =
        functions?.slice(0, 2).map((fn) => ({
            name: fn.name || null,
            weight: fn.weight || null,
            color: getPillColorValue(fn.label),
        })) ?? [];

    const hasHierarchy =
        (typeof hierarchy === "string" && hierarchy !== "") ||
        (typeof hierarchy === "object" && hierarchy !== null && hierarchy?.name !== "");

    return (
        <Box display="flex" alignItems="center" gap={0.5} margin={"4px 0px"}>
            <Tooltip
                arrow
                disableHoverListener={functionsLabels.length <= 1}
                title={
                    <Box display="flex" flexDirection="column">
                        {functionsLabels.map((fn, i) => (
                            <Typography key={i} variant="caption" sx={{ whiteSpace: "pre-line" }}>
                                {fn.name}: {fn.weight}%
                            </Typography>
                        ))}
                    </Box>
                }
                PopperProps={{ style: { zIndex: 2000 } }}
            >
                <Box display="inline-flex">
                    {functionsLabels?.map((fn, index) => (
                        <React.Fragment key={index}>
                            <Box
                                sx={{
                                    background: fn.color,
                                    borderTopLeftRadius: index < 1 ? 16 : 0,
                                    borderBottomLeftRadius: index < 1 ? 16 : 0,
                                    borderTopRightRadius: hasHierarchy ? 0 : 16,
                                    borderBottomRightRadius: hasHierarchy ? 0 : 16,
                                    pl: index === 0 ? 1 : 0,
                                    pr: index === 1 || functionsLabels.length === 1 ? 1 : 0,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={(theme: Theme) => ({
                                        color:
                                            fn.name === "Leadership"
                                                ? theme.palette.text.alt
                                                : theme.palette.text.secondary,
                                    })}
                                >
                                    {fn.name}
                                </Typography>
                            </Box>

                            {index < functionsLabels.length - 1 && (
                                <Box
                                    sx={(theme: Theme) => ({
                                        display: "inline-block",
                                        px: theme.spacing(0.8),
                                        background: `linear-gradient(90deg, ${fn.color} 30%, ${
                                            functionsLabels[index + 1].color
                                        } 70%)`,
                                    })}
                                >
                                    |
                                </Box>
                            )}
                        </React.Fragment>
                    ))}
                </Box>
            </Tooltip>

            <Box
                sx={(theme: Theme) => ({
                    display: "flex",
                    alignItems: "center",
                    borderTopRightRadius: 16,
                    borderBottomRightRadius: 16,
                    borderTopLeftRadius: functionsLabels.length ? 0 : 16,
                    borderBottomLeftRadius: functionsLabels.length ? 0 : 16,
                    backgroundColor: theme.palette.others.lightSkyBlue,
                    padding: theme.spacing(0, 0.75),
                })}
            >
                <Typography variant="caption" sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}>
                    {typeof hierarchy === "string" ? hierarchy : hierarchy?.name ?? ""}
                </Typography>
            </Box>
        </Box>
    );
};
